import { useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { mergeModules, timeAgo, LAUNCH_MODULE_META, MODULE_COLOR_RGB } from "@/lib/modules";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import {
  ArrowRight, Plus, FolderKanban, Upload, MessageCircle, CreditCard, Loader2,
  Image as ImageIcon, Globe, Search, Workflow, Code2, Activity as ActivityIcon,
} from "lucide-react";
import { toast } from "sonner";

const ACTIVITY_ICONS = {
  chat: { icon: MessageCircle, rgb: MODULE_COLOR_RGB.pink },
  image: { icon: ImageIcon, rgb: MODULE_COLOR_RGB.green },
  website: { icon: Globe, rgb: MODULE_COLOR_RGB.blue },
  research: { icon: Search, rgb: MODULE_COLOR_RGB.amber },
  swarm: { icon: Workflow, rgb: MODULE_COLOR_RGB.purple },
  code: { icon: Code2, rgb: MODULE_COLOR_RGB.cyan },
};

const USAGE_GROUPS = [
  { label: "AlphaCore AI", rgb: MODULE_COLOR_RGB.pink, match: (r) => r.startsWith("chat") || r.startsWith("voice") || r.startsWith("generation:k-create") },
  { label: "Image Generator", rgb: MODULE_COLOR_RGB.green, match: (r) => r.startsWith("image") || r.includes("k-visual") },
  { label: "Website Builder", rgb: MODULE_COLOR_RGB.blue, match: (r) => r.startsWith("website") },
  { label: "AI Research", rgb: MODULE_COLOR_RGB.amber, match: (r) => r.startsWith("research") || r.startsWith("insight") },
  { label: "Swarm Center", rgb: MODULE_COLOR_RGB.purple, match: (r) => r.startsWith("swarm") },
  { label: "AlphaCore Code", rgb: MODULE_COLOR_RGB.cyan, match: (r) => r.startsWith("code") },
];

function rgba(rgb, a) { return `rgba(${rgb.split(" ").join(",")},${a})`; }

function ModuleCard({ m }) {
  const disabled = m.status === "maintenance" || m.status === "coming_soon";
  const inner = (
    <div
      className="relative rounded-2xl p-5 h-full flex flex-col border transition-all duration-200 group-hover:-translate-y-0.5"
      style={{
        borderColor: rgba(m.rgb, 0.35),
        background: `linear-gradient(150deg, ${rgba(m.rgb, 0.10)} 0%, rgba(16,18,25,0.6) 55%)`,
        boxShadow: `0 0 0 0 transparent`,
      }}
      onMouseEnter={(e) => { e.currentTarget.style.boxShadow = `0 0 34px ${rgba(m.rgb, 0.18)}`; }}
      onMouseLeave={(e) => { e.currentTarget.style.boxShadow = "0 0 0 0 transparent"; }}
    >
      <m.icon className="h-9 w-9" strokeWidth={1.5} style={{ color: `rgb(${m.rgb})` }} />
      <div className="font-display text-base mt-4 tracking-tight">{m.name}</div>
      <p className="text-[13px] text-[#9DA3B4] mt-1.5 leading-relaxed flex-1">{m.description}</p>
      <div className="mt-4 flex items-center justify-between">
        {disabled ? (
          <span className="text-[10px] font-mono tracking-wider px-2 py-0.5 rounded-sm border border-white/15 text-white/50 uppercase">{m.status === "maintenance" ? "Maintenance" : "Coming soon"}</span>
        ) : <span />}
        <ArrowRight className="h-4 w-4 text-white/35 group-hover:text-white transition-colors" />
      </div>
    </div>
  );
  if (disabled) return <div className="group cursor-not-allowed opacity-70" data-testid={`module-card-${m.key}`}>{inner}</div>;
  return <Link to={m.route} className="group block" data-testid={`module-card-${m.key}`}>{inner}</Link>;
}

function UsageDonut({ segments, total }) {
  const sum = segments.reduce((a, s) => a + s.value, 0);
  let acc = 0;
  const stops = segments.filter((s) => s.value > 0).map((s) => {
    const from = (acc / sum) * 360; acc += s.value;
    const to = (acc / sum) * 360;
    return `rgb(${s.rgb}) ${from}deg ${to}deg`;
  });
  const bg = sum > 0 ? `conic-gradient(${stops.join(",")})` : "conic-gradient(rgba(255,255,255,0.08) 0deg 360deg)";
  return (
    <div className="relative w-28 h-28 shrink-0">
      <div className="absolute inset-0 rounded-full" style={{ background: bg }} />
      <div className="absolute inset-[10px] rounded-full bg-[#101219]" />
    </div>
  );
}

export default function Dashboard() {
  const { user, refreshUser } = useAuth();
  const navigate = useNavigate();
  const [smart, setSmart] = useState(null);
  const [modules, setModules] = useState(() => mergeModules(null));
  const [txs, setTxs] = useState([]);
  const [newOpen, setNewOpen] = useState(false);
  const [newName, setNewName] = useState("");
  const [creating, setCreating] = useState(false);

  useEffect(() => {
    api.get("/dashboard/smart").then((r) => setSmart(r.data)).catch(() => setSmart({}));
    api.get("/modules").then((r) => setModules(mergeModules(r.data.items))).catch(() => {});
    api.get("/credits/me").then((r) => setTxs(r.data.transactions || [])).catch(() => {});
  }, []);

  const usage = useMemo(() => {
    const spent = txs.filter((t) => t.delta < 0);
    const groups = USAGE_GROUPS.map((g) => ({ ...g, value: spent.filter((t) => g.match(t.reason || "")).reduce((a, t) => a + Math.abs(t.delta), 0) }));
    const totalUsed = spent.reduce((a, t) => a + Math.abs(t.delta), 0);
    return { groups, totalUsed };
  }, [txs]);

  const createProject = async () => {
    if (!newName.trim()) return toast.error("Give your project a name");
    setCreating(true);
    try {
      const { data } = await api.post("/projects", { name: newName.trim() });
      toast.success("Project created");
      setNewOpen(false); setNewName("");
      navigate(`/projects/${data.project_id}`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not create project");
    } finally { setCreating(false); }
  };

  const greeting = smart?.greeting || "Welcome back";
  const recentProjects = smart?.recent_projects || [];
  const recentActivity = smart?.recent_activity || [];
  const suggestions = smart?.suggestions || [];

  return (
    <div className="p-4 sm:p-6 lg:p-8 max-w-[1500px] mx-auto kr-page" data-testid="smart-dashboard">
      {/* Welcome */}
      <div className="flex items-end justify-between flex-wrap gap-4">
        <div>
          <h1 className="font-display text-2xl sm:text-3xl lg:text-4xl tracking-tight" data-testid="dashboard-greeting">
            {greeting}, {user?.name?.split(" ")[0]} <span className="inline-block">👋</span>
          </h1>
          <p className="text-[#9DA3B4] text-sm sm:text-base mt-2">What will you create today?</p>
        </div>
        <Button onClick={() => setNewOpen(true)} className="rounded-xl h-10 px-5 font-semibold text-white border-0" style={{ background: "linear-gradient(90deg,#FF3AA8,#8A52FF)" }} data-testid="new-project-btn">
          <Plus className="h-4 w-4 mr-1.5" /> New Project
        </Button>
      </div>

      {/* Module cards */}
      <div className="mt-7 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 2xl:grid-cols-6 gap-4" data-testid="module-cards">
        {modules.map((m) => <ModuleCard key={m.key} m={m} />)}
      </div>

      {/* Suggestions */}
      {suggestions.length > 0 && (
        <div className="mt-6 flex flex-wrap gap-3" data-testid="dashboard-suggestions">
          {suggestions.map((s) => (
            <button key={s.kind} onClick={() => navigate(s.cta?.to || "/dashboard")} className="kr-card px-4 py-3 flex items-center gap-3 text-left hover:border-white/20">
              <span className="w-1.5 h-1.5 rounded-full ac-rainbow-bg shrink-0" />
              <div>
                <div className="text-sm font-semibold">{s.title}</div>
                <div className="text-xs text-white/45">{s.reason}</div>
              </div>
              <ArrowRight className="h-3.5 w-3.5 text-white/35 ml-2" />
            </button>
          ))}
        </div>
      )}

      {/* Data sections */}
      <div className="mt-7 grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-4">
        {/* Recent projects */}
        <div className="kr-card p-5" data-testid="recent-projects-card">
          <div className="flex items-center justify-between">
            <div className="font-display text-sm">Recent Projects</div>
            <Link to="/projects" className="text-xs text-white/45 hover:text-white">View all</Link>
          </div>
          <div className="mt-4 space-y-3">
            {recentProjects.slice(0, 4).map((p) => (
              <Link key={p.project_id} to={`/projects/${p.project_id}`} className="flex items-center gap-3 group" data-testid={`recent-project-${p.project_id}`}>
                <span className="w-9 h-9 rounded-lg flex items-center justify-center border border-white/10 shrink-0" style={{ background: "rgba(255,255,255,0.03)" }}>
                  <FolderKanban className="h-4 w-4" style={{ color: p.color || "#3AB4FF" }} />
                </span>
                <div className="min-w-0">
                  <div className="text-sm truncate group-hover:text-white text-white/85">{p.name}</div>
                  <div className="text-[11px] text-white/35">Created {timeAgo(p.created_at)}</div>
                </div>
              </Link>
            ))}
            {recentProjects.length === 0 && (
              <div className="py-6 text-center">
                <div className="text-sm text-white/40">No projects yet.</div>
                <button onClick={() => setNewOpen(true)} className="text-xs mt-1 text-[#FF3AA8] hover:underline">Create your first project</button>
              </div>
            )}
          </div>
        </div>

        {/* Usage overview */}
        <div className="kr-card p-5" data-testid="usage-overview-card">
          <div className="font-display text-sm">Usage Overview</div>
          <div className="mt-4 flex items-center gap-4">
            <UsageDonut segments={usage.groups} />
            <div>
              <div className="text-xs text-white/40">Total Credits</div>
              <div className="font-display text-2xl mt-0.5" data-testid="dashboard-credits">{user?.credits?.toLocaleString() ?? 0}</div>
              <div className="text-[11px] text-white/35 mt-1">{usage.totalUsed.toLocaleString()} used all-time</div>
            </div>
          </div>
          <div className="mt-4 space-y-1.5">
            {usage.groups.filter((g) => g.value > 0).map((g) => (
              <div key={g.label} className="flex items-center justify-between text-xs">
                <span className="flex items-center gap-2 text-white/60"><span className="w-2 h-2 rounded-full" style={{ background: `rgb(${g.rgb})` }} /> {g.label}</span>
                <span className="font-mono text-white/70">{g.value.toLocaleString()}</span>
              </div>
            ))}
            {usage.totalUsed === 0 && <div className="text-xs text-white/35">No usage yet — start creating.</div>}
          </div>
        </div>

        {/* Quick actions */}
        <div className="kr-card p-5" data-testid="quick-actions-card">
          <div className="font-display text-sm">Quick Actions</div>
          <div className="mt-4 space-y-1">
            <QuickAction icon={Plus} label="Create New Project" onClick={() => setNewOpen(true)} testid="qa-new-project" rgb={MODULE_COLOR_RGB.pink} />
            <QuickAction icon={Upload} label="Upload Files" onClick={() => navigate("/files")} testid="qa-upload" rgb={MODULE_COLOR_RGB.green} />
            <QuickAction icon={MessageCircle} label="Open AlphaCore AI" onClick={() => navigate("/agents/create")} testid="qa-chat" rgb={MODULE_COLOR_RGB.blue} />
            <QuickAction icon={CreditCard} label="View Plans & Credits" onClick={() => navigate("/billing")} testid="qa-billing" rgb={MODULE_COLOR_RGB.amber} />
          </div>
        </div>

        {/* Activity feed */}
        <div className="kr-card p-5" data-testid="activity-feed-card">
          <div className="flex items-center justify-between">
            <div className="font-display text-sm">Activity Feed</div>
            <ActivityIcon className="h-3.5 w-3.5 text-white/30" />
          </div>
          <div className="mt-4 space-y-3">
            {recentActivity.slice(0, 5).map((a) => {
              const meta = ACTIVITY_ICONS[a.module] || { icon: ActivityIcon, rgb: "156 163 175" };
              return (
                <div key={a.event_id} className="flex items-start gap-3">
                  <span className="w-8 h-8 rounded-lg flex items-center justify-center shrink-0" style={{ background: rgba(meta.rgb, 0.12) }}>
                    <meta.icon className="h-3.5 w-3.5" style={{ color: `rgb(${meta.rgb})` }} />
                  </span>
                  <div className="min-w-0">
                    <div className="text-xs text-white/80 truncate">{a.target_name || a.action}</div>
                    <div className="text-[11px] text-white/35">{timeAgo(a.created_at)}</div>
                  </div>
                </div>
              );
            })}
            {recentActivity.length === 0 && <div className="text-xs text-white/35 py-4">Nothing yet — your activity will appear here.</div>}
          </div>
        </div>
      </div>

      <div className="mt-10 pb-4 text-center text-xs text-white/25">© 2026 AlphaCore. All rights reserved.</div>

      {/* New Project dialog */}
      <Dialog open={newOpen} onOpenChange={setNewOpen}>
        <DialogContent className="bg-[#101219] border-white/10 text-white max-w-sm">
          <DialogHeader><DialogTitle className="font-display">New Project</DialogTitle></DialogHeader>
          <Input
            value={newName}
            onChange={(e) => setNewName(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && createProject()}
            placeholder="Project name…"
            className="bg-[#07080B] border-white/10 mt-2"
            data-testid="new-project-name-input"
            autoFocus
          />
          <Button onClick={createProject} disabled={creating} className="w-full mt-3 rounded-xl font-semibold text-white border-0" style={{ background: "linear-gradient(90deg,#FF3AA8,#8A52FF)" }} data-testid="new-project-create-btn">
            {creating ? <Loader2 className="h-4 w-4 animate-spin" /> : "Create project"}
          </Button>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function QuickAction({ icon: Icon, label, onClick, testid, rgb }) {
  return (
    <button onClick={onClick} className="w-full flex items-center gap-3 px-2.5 py-2.5 rounded-lg hover:bg-white/[0.04] text-left min-h-[44px]" data-testid={testid}>
      <span className="w-8 h-8 rounded-lg flex items-center justify-center" style={{ background: rgba(rgb, 0.12) }}>
        <Icon className="h-3.5 w-3.5" style={{ color: `rgb(${rgb})` }} />
      </span>
      <span className="text-sm text-white/80 flex-1">{label}</span>
      <ArrowRight className="h-3.5 w-3.5 text-white/30" />
    </button>
  );
}
